
import { ENTITY_LABELS } from "../../../utils/labelMapping";
import React, { useMemo, useState } from "react";
import {
  transformSourceDataForMultibarChart,
  getLLMInfo,
} from "../../../utils/helpers";
import FilterSelection from "../common/FilterSelection";
import ReTableView from "../common/ReTableView";
import CustomDateFilter from "../common/CustomDateFilter";
import { Button } from "@mui/material";
import {
  downloadExcelV2,
  downloadExcelV3,
  prepareSourceDataForExcel,
} from "../../../utils/monitoring-helpers";

const SourceComponent = ({
  startDate,
  endDate,
  indexScoreDataForLineChart,
  positiveAttSource,
  negativeAttSource,
  categoryDimensionSource,
  categoryDimensionRatingSource,
  coreAttSourceData,
  reachSourceData,
  sentimentSourceData,
  brandProduct,
  isProductDashboard,
  sourceFilter,
  handleFilterChange,
  sourceAlias,
  activeTarget,
  updateCustomFilterState,
  frequency,
  customDatesToDisplay,
  selectedInput,
}) => {
  const labelMapping = {
    LLM: "llm",
    PERSONA: "persona",
    CATEGORY: "category",
  };

  const reporting =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("reporting") === "true"
      : false;

  // helper transform
  const transform = (d) => transformSourceDataForMultibarChart(d, true);

  // prepare datasets
  const positiveAttSourceBarChartData = transform(positiveAttSource);
  const negativeAttSourceBarChartData = transform(negativeAttSource);
  const categoryDimSourceBarChartData = transform(categoryDimensionSource) || [];
  const categoryDimRatingSourceBarChartData = transform(categoryDimensionRatingSource);
  const coreAttSourceBarChartData = transform(coreAttSourceData);
  const reachSourceBarChartData = transform(reachSourceData);
  const sentimentSourceBarChartData = transform(sentimentSourceData);

  // dropdown options
  const brandDropdownOptions = [
    { value: "Sentiment", label: "Sentiment" },
    { value: "Positive Attributes", label: "Positive Attributes" },
    { value: "Negative Attributes", label: "Negative Attributes" },
    { value: "Purchase Criteria", label: ENTITY_LABELS.purchaseCriteria },
    { value: "Purchase Criteria Rating", label: `${ENTITY_LABELS.purchaseCriteria} Rating` },
    { value: "Brand Pillars Rating", label: `${ENTITY_LABELS.brandPillar} Rating` },
  ];

  const categoryDropdownOptions = [
    { value: "Visibility", label: ENTITY_LABELS.rank },
  ];

  // default selected
  const [selectedBrandSources, setSelectedBrandSources] = useState(
    brandDropdownOptions.map((o) => o.value)
  );
  const [selectedCategorySources, setSelectedCategorySources] = useState(
    categoryDropdownOptions.map((o) => o.value)
  );

  // map dropdown -> dataset
  const brandSourceMap = useMemo(
    () => ({
      Sentiment: sentimentSourceBarChartData,
      "Positive Attributes": positiveAttSourceBarChartData,
      "Negative Attributes": negativeAttSourceBarChartData,
      "Brand Pillars Rating": coreAttSourceBarChartData,
      "Purchase Criteria": categoryDimSourceBarChartData,
      "Purchase Criteria Rating": categoryDimRatingSourceBarChartData,
    }),
    [
      sentimentSourceBarChartData,
      positiveAttSourceBarChartData,
      negativeAttSourceBarChartData,
      coreAttSourceBarChartData,
      categoryDimSourceBarChartData,
      categoryDimRatingSourceBarChartData,
    ]
  );

  const categorySourceMap = useMemo(
    () => ({
      Visibility: reachSourceBarChartData,
    }),
    [reachSourceBarChartData]
  );

  // merge function
  function mergeMultipleArraysV2(...arrays) {
    const merged = {};
    arrays.flat()?.forEach((item) => {
      if (item && typeof item.name === "string") {
        const { name, ...llms } = item;
        if (!merged[name]) merged[name] = {};

        Object.entries(llms).forEach(([llm, llmData]) => {
          if (!merged[name][llm]) {
            merged[name][llm] = {
              count: llmData.count || 0,
              aliases: llmData.aliases ? llmData.aliases.map(a => ({ ...a })) : [],
            };
          } else {
            merged[name][llm].count += llmData.count || 0;

            llmData.aliases?.forEach((alias) => {
              const existing = merged[name][llm].aliases.find(
                (a) => a.source_alias_url === alias.source_alias_url
              );
              if (existing) {
                existing.alias_signal_strength =
                  Number(existing.alias_signal_strength || 0) +
                  Number(alias.alias_signal_strength || 0);
                existing.alias_query_count =
                  Number(existing.alias_query_count || 0) +
                  Number(alias.alias_query_count || 0);
                existing.alias_absolute_count =
                  Number(existing.alias_absolute_count || 0) +
                  Number(alias.alias_absolute_count || 0);
              } else {
                merged[name][llm].aliases.push({ ...alias });
              }
            });

            merged[name][llm].count = merged[name][llm].aliases.length;
          }
        });
      }
    });

    return Object.entries(merged).map(([name, llms]) => ({ name, ...llms }));
  }

  // sort function
  function sortArraysByLLMFrequencyV2(arrays) {
    return arrays?.map((array) => {
      if (!array) return array;
      const llmKeys = Array.from(new Set(array.flatMap(Object.keys))).filter((key) => key !== "name");
      return array.sort((a, b) => {
        const sumCounts = (obj) => llmKeys.reduce(
          (sum, key) => sum + (obj[key]?.count ? Number(obj[key].count) : 0),
          0
        );
        return sumCounts(b) - sumCounts(a);
      });
    });
  }

  // deep clone helper using structuredClone or fallback
  const deepClone = (obj) => {
    if (typeof structuredClone === "function") return structuredClone(obj);
    return JSON.parse(JSON.stringify(obj));
  };

  // build aggregated arrays safely
  const aggregatedBrandSources = useMemo(() => {
    const datasets = (selectedBrandSources || [])
      .map((key) => brandSourceMap[key])
      .filter(Boolean)
      .map(deepClone); // clone to prevent mutation
    if (datasets.length === 0) return [];
    return mergeMultipleArraysV2(...datasets);
  }, [selectedBrandSources, brandSourceMap]);

  const aggregatedCategorySources = useMemo(() => {
    const datasets = (selectedCategorySources || [])
      .map((key) => categorySourceMap[key])
      .filter(Boolean)
      .map(deepClone); // clone to prevent mutation
    if (datasets.length === 0) return [];
    return mergeMultipleArraysV2(...datasets);
  }, [selectedCategorySources, categorySourceMap]);

  const brandSourcesChartDataArray = sortArraysByLLMFrequencyV2([aggregatedBrandSources]);
  const categorySourcesChartDataArray = sortArraysByLLMFrequencyV2([aggregatedCategorySources]);

  // export
  const handleExportAll = () => {
    const combinedData = [...brandSourcesChartDataArray, ...categorySourcesChartDataArray].flat();
    const preparedData = prepareSourceDataForExcel(combinedData, selectedInput, labelMapping, sourceFilter);
    downloadExcelV2(preparedData, labelMapping[sourceFilter?.value]);
  };

  const handleExportDomain = () => {
    const combinedData = [...brandSourcesChartDataArray, ...categorySourcesChartDataArray].flat();
    const preparedData = prepareSourceDataForExcel(combinedData, selectedInput, labelMapping, sourceFilter);
    downloadExcelV3(preparedData, labelMapping[sourceFilter?.value]);
  };

  // Get LLM info - no need for isChecked for source tab - use same logic as payload
  // Use customDatesToDisplay (applied dates) instead of state dates
  const appliedStartDate = customDatesToDisplay?.startDate || startDate;
  const appliedEndDate = customDatesToDisplay?.endDate || endDate;
  const effectiveEndDate = activeTarget === "Recent" ? appliedEndDate : appliedStartDate;
  const llmInfo = getLLMInfo(indexScoreDataForLineChart, false, appliedStartDate, effectiveEndDate);

  return (
    <>
      {frequency === "Custom" && (
        <CustomDateFilter
          activeTarget={activeTarget}
          handleChange={updateCustomFilterState}
          customDatesToDisplay={customDatesToDisplay}
        />
      )}

      {llmInfo && sourceFilter.value !== "LLM" && (
        <div style={{
          fontSize: "15px",
          marginLeft: '10px',
          marginRight: '10px',
          marginBottom: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            padding: '6px 16px',
            backgroundColor: '#e3f2fd',
            border: '1px solid #90caf9',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '600',
            color: '#1565c0',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <span style={{ color: '#0d47a1', marginRight: '8px' }}>LLM:</span>
            {llmInfo}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        {!reporting && (
          <div className=" customTabContent  col-md-3 mt-2 ">
            <div style={{ position: "relative" }} className="input-group input-group-lg mb-2">
              <span className="input-group-text" id="basic-addon1">
                <i className="feather icon-target"></i>
              </span>
              <FilterSelection
                options={[
                  { value: "LLM", label: "Compare by LLM" },
                  { value: "PERSONA", label: "Compare by Persona" },
                  { value: "CATEGORY", label: "Compare by Category" },
                ]}
                selectedOptions={sourceFilter}
                handleChange={(selectedOptions) => handleFilterChange(selectedOptions)}
              />
            </div>
          </div>
        )}

        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <Button onClick={handleExportAll}>Export All Sources</Button>
          <Button onClick={handleExportDomain}>Export Domain Sources</Button>
        </div>
      </div>

      {brandSourcesChartDataArray?.map((data, index) => (
        <div key={`brand-table-${index}`}>
          <ReTableView
            type={isProductDashboard ? ENTITY_LABELS.product : ENTITY_LABELS.brand}
            title={`${ENTITY_LABELS.brand} Query Sources`}
            data={data}
            sourceAlias={sourceAlias}
            selectedInput={selectedInput}
            dropdownOptions={brandDropdownOptions}
            selectedDropdownValues={selectedBrandSources}
            onDropdownChange={(newVal) =>
              setSelectedBrandSources(Array.isArray(newVal) ? newVal : (newVal?.target ? newVal.target.value : selectedBrandSources))
            }
          />
        </div>
      ))}

      <hr />

      {categorySourcesChartDataArray?.map((data, index) => (
        <div key={`category-table-${index}`}>
          <ReTableView
            type="Category"
            title="Category Query Sources"
            data={data}
            sourceAlias={sourceAlias}
            selectedInput={selectedInput}
            dropdownOptions={categoryDropdownOptions}
            selectedDropdownValues={selectedCategorySources}
            onDropdownChange={(newVal) =>
              setSelectedCategorySources(Array.isArray(newVal) ? newVal : (newVal?.target ? newVal.target.value : selectedCategorySources))
            }
          />
        </div>
      ))}
    </>
  );
};

export default SourceComponent;